<template>
	<div class="flow-checkbox-wrapper" :disabled="disabled">
		<div>
			<div
				class="flow-checkbox"
				:tabindex="tabindex"
				:checked="innerChecked"
				:indeterminate="indeterminate"
				:disabled="disabled"
				:state="state"
				:size="size"
				@click="handleClick($event)"
			>
				<svg
					v-if="innerChecked && !indeterminate"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
				>
					<path d="M6,13.223,8.45,16.7a1.049,1.049,0,0,0,1.707.051L18,6.828" />
				</svg>
				<svg
					v-if="indeterminate && !innerChecked"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
				>
					<path d="M4 12 L20 12 Z" />
				</svg>
			</div>
			<div class="flow-checkbox-label" @click="handleClick($event)">
				<!-- @slot Used to display checkbox label-->
				<slot />
			</div>
		</div>
		<div v-if="$slots['child-checkboxes']" class="flow-child-checkbox">
			<!-- @slot Used to display checkbox hierarchy-->
			<slot name="child-checkboxes" />
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
/**
 * The checkbox control allows user to make multiple selections.
 */
export default defineComponent({
	name: "Checkbox",
	props: {
		/**
		 * true | false
		 */
		checked: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		indeterminate: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		disabled: {
			type: Boolean,
			default: false
		},
		/**
		 * tabindex number
		 *  */
		tabindex: {
			type: Number,
			default: -1
		},
		/**
		 *  'primary' | 'secondary' | 'success' | 'error' | 'warning'
		 */
		state: {
			type: String,
			default: "primary",
			validator(value) {
				return ["primary", "secondary", "success", "error", "warning", "default"].includes(value);
			}
		},
		/**
		 * 'small' | 'medium' | 'large'
		 */
		size: {
			type: String,
			default: "small",
			validator(value) {
				return ["small", "medium", "large"].includes(value);
			}
		}
	},
	computed: {
		innerChecked: {
			// getter
			get() {
				return this.checked;
			},
			// setter
			set(newValue) {
				/**
				 * Whenever value is updated.<br/>
				 * use 'checked.sync' in parent component to achieve 2 way binding.
				 *
				 * @event update:value
				 */
				this.$emit("update:checked", newValue);
			}
		}
	},
	methods: {
		handleClick(e) {
			if (!this.disabled) {
				const currentValue = this.innerChecked;
				this.innerChecked = !currentValue;
				/**
				 * click event
				 *
				 * @event click
				 */

				this.$emit("click", e);
				/**
				 * input event
				 * @event input
				 */
				this.$emit("input", !currentValue, this.indeterminate);
			}
		}
	}
});
</script>
<style lang="scss">
@mixin flow-checkbox($state-colors, $font-color, $primary, $tick) {
	div.flow-checkbox-wrapper {
		> div {
			> div.flow-checkbox {
				border: 1px solid $font-color;
				&[checked],
				&[indeterminate] {
					@each $state, $color in $state-colors {
						&[state="#{$state}"] {
							background-color: $color;
							border: 1px solid $color;
						}
					}
				}
				@each $state, $color in $state-colors {
					@if $state == error {
						&[state="#{$state}"] {
							border: 1px solid $color;
						}
					}
				}
				> svg {
					> path {
						fill: none;
						stroke: $tick;
					}
				}
				&:not([disabled]) {
					&:focus {
						outline: none;
						&:before {
							background-color: transparent;
							border: 1px solid $primary;
						}
					}
				}
			}
			> div.flow-checkbox-label {
				color: $font-color;
			}
		}
	}
}

$sizes: (
	"large": 32px,
	"medium": 24px,
	"small": 16px
);

div.flow-checkbox-wrapper {
	display: inline-flex;
	flex-direction: column;
	cursor: pointer;
	position: relative;
	> div {
		display: flex;
		flex-direction: row;
		position: relative;
		// align-items: center;
		> div.flow-checkbox {
			@each $name, $size in $sizes {
				&[size="#{$name}"] {
					height: $size;
					width: $size;
					flex: 0 0 $size;

					> svg {
						height: $size;
						width: $size;
					}
				}
			}
			border-radius: 2px;
			margin-right: 12px;
			display: inline-flex;
			align-items: center;
			position: relative;
			outline: none;
			justify-content: center;

			> svg {
				> path {
					stroke-linecap: round;
					stroke-linejoin: round;
					stroke-width: 2.5px;
				}
			}

			&:not([disabled]) {
				&:focus {
					&:before {
						content: "";
						position: absolute;
						top: -3px;
						right: -3px;
						bottom: -3px;
						left: -3px;
						border-radius: 4px;
					}
				}
			}
		}

		> div.flow-checkbox-label {
			font-size: 12px;
			line-height: 16px;
			font-weight: normal;
			word-break: break-all;
		}
	}

	&[disabled] {
		opacity: 0.5;
		cursor: not-allowed;
	}

	> div.flow-child-checkbox {
		display: flex;
		flex-direction: column;
		padding-left: 28px;
		// padding-top: 8px;

		> div.flow-checkbox-wrapper {
			margin-top: 8px;
		}
	}
}
div.flow-checkbox-wrapper + div.flow-checkbox-wrapper {
	margin: 8px 0px 0px 0px;
}
div.flow-form-field-label-wrapper + div.flow-checkbox-wrapper {
	margin: 4px 0px 0px 0px;
}
@include flow-checkbox(
	(
		"default": var(--dashboard-primary),
		"primary": var(--dashboard-primary),
		"secondary": var(--dashboard-secondary),
		"success": var(--dashboard-success),
		"error": var(--dashboard-danger),
		"warning": var(--dashboard-warning)
	),
	var(--dashboard-font),
	var(--dashboard-primary),
	var(--dashboard-background)
);

div.sideout-container {
	@include flow-checkbox(
		(
			"default": var(--slideout-primary),
			"primary": var(--slideout-primary),
			"secondary": var(--slideout-secondary),
			"success": var(--slideout-success),
			"error": var(--slideout-danger),
			"warning": var(--slideout-warning)
		),
		var(--slideout-font),
		var(--slideout-primary),
		var(--slideout-background)
	);
}
</style>
